import { useEffect, useState } from 'react';
import type { Meta } from './api';
import { ALL, readViewPrefs, writeViewPrefs } from './viewPrefs';

/**
 * The board's view selections, remembered across refreshes.
 *
 * Deliberately localStorage rather than the URL: the ask is that a refresh keeps your
 * place, the app has no router, and a query string would additionally have to be kept in
 * sync with history. A linkable `?epic=…` is a reasonable future move, not this one.
 *
 * The parsing and storage live in `viewPrefs.ts`, React-free, so they stay testable
 * without a DOM.
 */
export function useViewPrefs(meta: Meta | null) {
  // useState rather than useMemo: React documents useMemo as a hint it may discard and
  // recompute, and this wants an actual once-per-mount read.
  const [stored] = useState(readViewPrefs);
  const [tab, setTab] = useState(stored?.tab ?? 0);
  const [epic, setEpic] = useState(stored?.epic ?? ALL);
  const [status, setStatus] = useState(stored?.status ?? ALL);

  useEffect(() => writeViewPrefs({ tab, epic, status }), [tab, epic, status]);

  /**
   * A stored value the board no longer offers is dropped as soon as `meta` says so.
   * Without this, an epic that has been renamed or retired filters every ticket out and
   * the board reads as empty — indistinguishable from having no tickets, and blamed on
   * the data rather than on a filter the user cannot see is still applied.
   */
  useEffect(() => {
    if (!meta) return;
    if (epic !== ALL && !meta.epics.includes(epic)) setEpic(ALL);
    if (status !== ALL && !meta.statuses.includes(status)) setStatus(ALL);
  }, [meta, epic, status]);

  return { tab, setTab, epic, setEpic, status, setStatus };
}
